// src/components/MyPhonePanel.jsx — the agent registers the phone their office will recognise.
//
// OWNER, 2026-09-05: "verify with the router — if their device is connected to this router then
// accept." The office reports which devices are on its Wi-Fi (0008); this is where an agent says
// which one is theirs, without which the office sees a network full of addresses matching nobody
// — on screen indistinguishable from an empty office.
//
// ⚠️ THE ADDRESS CANNOT BE READ FOR THEM. A web page cannot see the phone's Wi-Fi address any
// more than it can see the router's, so the agent copies it from their own settings. That is why
// this panel is mostly INSTRUCTIONS: the typing is the hard part, and the two platforms hide it
// in different places under different names.
//
// ⚠️ And it is validated against reality, not against a format: when the office reporter is
// running, the server refuses an address the office cannot currently see. A typo therefore fails
// here and now with something a person can act on, instead of being stored and silently never
// matching — which would surface weeks later as "the check is broken".
import { useEffect, useState } from 'react'
import { t } from '../lib/i18n.js'
import { Icon, IconText } from './Icon.jsx'
import { getMyOfficeDevices, registerMyOfficeDevice, revokeMyOfficeDevice } from '../db/index.js'

/** The pure view — everything this panel decides, with no fetching, so it can be rendered. */
export function MyPhonePanelView({ devices, err, busy, mac, label, onMac, onLabel, onAdd, onRevoke, help, onHelp }) {
  const clean = (mac || '').replace(/[^0-9a-fA-F]/g, '')
  const ready = clean.length === 12
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-my-phone>
      <div className="text-xs font-medium text-muted-foreground">{t('myphone_title')}</div>

      {(devices || []).map(d => (
        <div key={d.id} className="flex items-center gap-2 text-xs" data-my-phone-row={d.mac}>
          {/* Seen or not seen is the whole story of whether this will work, so it leads. */}
          <span className={d.seen_now ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}>
            {/* 'eye-off', not a neutral dash: the office not seeing it is the whole reason the
                row is worth reading. ⚠️ Both names must exist in Icon.jsx's registry — an absent
                one renders nothing and the row silently loses its state (that file's own header
                records months of exactly this). 'minus' was the first choice and does not exist. */}
            <Icon name={d.seen_now ? 'check' : 'eye-off'} size={12} />
          </span>
          <span className="font-mono">{(d.mac.match(/../g) || []).join(':')}</span>
          {d.label && <span className="text-muted-foreground">{d.label}</span>}
          <span className={d.seen_now ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}>
            {d.seen_now ? t('myphone_seen') : t('myphone_not_seen')}
          </span>
          <button type="button" className="ml-auto text-muted-foreground hover:text-destructive"
            disabled={busy} data-revoke={d.id} onClick={() => onRevoke(d.id)}>{t('myphone_remove')}</button>
        </div>
      ))}

      {!devices?.length && <p className="text-[11px] text-muted-foreground">{t('myphone_none')}</p>}

      <div className="flex items-center gap-2 flex-wrap">
        <input value={mac} onChange={e => onMac(e.target.value)} placeholder="aa:bb:cc:dd:ee:ff"
          inputMode="text" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          className="rounded-lg border border-input bg-background px-2 py-1 text-xs font-mono w-44" data-mac-input />
        <input value={label} onChange={e => onLabel(e.target.value)} placeholder={t('myphone_label_ph')}
          className="rounded-lg border border-input bg-background px-2 py-1 text-xs w-32" />
        <button type="button" disabled={!ready || busy} data-add-phone onClick={onAdd}
          className={`text-xs px-2 py-1 rounded-lg border ${ready && !busy
            ? 'border-primary/50 bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
          {t('myphone_add')}
        </button>
        <button type="button" className="text-xs text-primary hover:underline" data-phone-help onClick={onHelp}>
          {t('myphone_where')}
        </button>
      </div>

      {/* Shown on demand rather than always: it is four lines of settings-menu navigation that an
          agent needs exactly once, and permanently on screen it would bury the state above it. */}
      {help && (
        <div className="text-[11px] text-muted-foreground space-y-1 border-t border-border/50 pt-2">
          <p><b>iPhone:</b> {t('myphone_help_ios')}</p>
          <p><b>Android:</b> {t('myphone_help_android')}</p>
          <p className="text-amber-600 dark:text-amber-400">{t('myphone_help_warning')}</p>
        </div>
      )}

      {err && <p className="text-[11px] text-destructive"><IconText>{'⚠️ ' + err}</IconText></p>}
    </div>
  )
}

// `relevant` is true when the office's verdict came through the DEVICE path at all — the agent is
// being let in by a phone, or refused because none was seen. Everywhere else the control would be
// a door with nothing behind it: an office with no reporter accepts a registration and then never
// uses it, so offering it there advertises a check that is not running (diagnosis rule 6).
// An agent who already has a phone registered always sees the panel, or revoking would be
// unreachable the moment their office's reporter stopped.
export function MyPhonePanel({ agentId, relevant }) {
  const [devices, setDevices] = useState(null)
  const [mac, setMac] = useState('')
  const [label, setLabel] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [help, setHelp] = useState(false)
  const [ver, setVer] = useState(0)

  useEffect(() => {
    if (!agentId) return
    let gone = false
    getMyOfficeDevices(agentId).then(d => { if (!gone) setDevices(d || []) }).catch(() => { if (!gone) setDevices([]) })
    return () => { gone = true }
  }, [agentId, ver])

  const run = async (fn) => {
    setBusy(true); setErr('')
    try { await fn(); setVer(v => v + 1) }
    // The server's refusals are written to be read by the agent ("the office cannot see that
    // device right now…"), so they are shown as they are rather than replaced with a generic one.
    catch (e) { setErr(e.message || String(e)) }
    finally { setBusy(false) }
  }

  // `devices === null` means the fetch has not answered; rendering nothing then, rather than an
  // empty panel that fills in, keeps a registered phone from flashing "you have none".
  if (!relevant && !devices?.length) return null

  return (
    <MyPhonePanelView
      devices={devices} err={err} busy={busy} mac={mac} label={label} help={help}
      onMac={setMac} onLabel={setLabel} onHelp={() => setHelp(h => !h)}
      onAdd={() => run(async () => { await registerMyOfficeDevice(agentId, mac, label); setMac(''); setLabel('') })}
      onRevoke={id => run(() => revokeMyOfficeDevice(id, agentId))}
    />
  )
}
